/**
 * Multi-professional meeting proposals on maintenance_requests.
 * Firestore field: professionalMeetingProposals (array, max 5 unique professionalId).
 * Legacy: meetingOptions + proposedByProfessional (single pro) — still read via normalizeMeetingProposals.
 */

import { Timestamp, FieldValue } from 'firebase/firestore';

export const MAX_PROFESSIONAL_MEETING_PROPOSALS = 5;

function coerceProposalTimestamp(value) {
  if (value == null) return Timestamp.fromDate(new Date());
  if (value instanceof FieldValue) return Timestamp.fromDate(new Date());
  if (value instanceof Timestamp) return value;
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) return Timestamp.fromDate(d);
    } catch {
      /* ignore */
    }
  }
  if (typeof value.seconds === 'number') {
    return new Timestamp(value.seconds, typeof value.nanoseconds === 'number' ? value.nanoseconds : 0);
  }
  return Timestamp.fromDate(new Date());
}

/**
 * Firestore forbids FieldValue.sentinel values (e.g. serverTimestamp) under array fields.
 * Call before writing `professionalMeetingProposals` in a transaction or updateDoc.
 *
 * @param {Array<{ professionalId?: string, businessName?: string, proposedAt?: unknown, options?: unknown[] }>} proposals
 */
export function sanitizeProfessionalMeetingProposalsForWrite(proposals) {
  if (!Array.isArray(proposals)) return [];
  return proposals
    .map((p) => ({
      professionalId: String(p?.professionalId || p?.id || '').trim(),
      businessName: String(p?.businessName || '').trim() || 'Professional',
      proposedAt: coerceProposalTimestamp(p?.proposedAt),
      options: Array.isArray(p?.options)
        ? p.options.map((o) => ({
            date: String(o?.date ?? ''),
            time: String(o?.time ?? ''),
            accepted: o?.accepted === true,
          }))
        : [],
    }))
    .filter((p) => p.professionalId);
}

export function normalizeMeetingProposals(req) {
  if (!req) return [];
  const arr = req.professionalMeetingProposals;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr
      .map((p) => ({
        professionalId: p.professionalId || p.id,
        businessName: p.businessName || '',
        proposedAt: p.proposedAt,
        options: Array.isArray(p.options)
          ? p.options.map((o) => ({
              date: o.date,
              time: o.time,
              accepted: o.accepted === true,
            }))
          : [],
      }))
      .filter((p) => p.professionalId);
  }
  if (
    req.proposedByProfessional?.id &&
    Array.isArray(req.meetingOptions) &&
    req.meetingOptions.length > 0
  ) {
    return [
      {
        professionalId: req.proposedByProfessional.id,
        businessName: req.proposedByProfessional.businessName || '',
        proposedAt: req.meetingProposedAt,
        options: req.meetingOptions.map((o) => ({
          date: o.date,
          time: o.time,
          accepted: o.accepted === true,
        })),
      },
    ];
  }
  return [];
}

export function countProposalProfessionals(req) {
  return normalizeMeetingProposals(req).length;
}

export function getProposalForProfessional(req, professionalUid) {
  return normalizeMeetingProposals(req).find((p) => p.professionalId === professionalUid) || null;
}

export function hasPendingMeetingProposals(req) {
  if (req?.acceptedMeetingTime) return false;
  return normalizeMeetingProposals(req).length > 0;
}

/** True if `professionalId` is on this request's preferred-first snapshot (`preferredOfferProIds`). */
export function isProfessionalOnPreferredOfferList(req, professionalId) {
  const raw = req?.preferredOfferProIds;
  if (!Array.isArray(raw) || professionalId == null) return false;
  const pid = String(professionalId).trim();
  if (!pid) return false;
  return raw.some((id) => String(id || '').trim() === pid);
}

/** True if any option in any proposal is marked accepted (should be rare if we clear on accept). */
export function hasAnyAcceptedProposalOption(req) {
  return normalizeMeetingProposals(req).some((p) =>
    (p.options || []).some((o) => o.accepted)
  );
}

/**
 * Parse meeting `date` + `time` from HTML date/time inputs (YYYY-MM-DD, HH:mm).
 * Uses the viewer's local timezone as the calendar frame (same as `new Date(y, m, d, h, mi)`).
 */
export function parseMeetingDateTimeLocalParts(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr).trim());
  const tm = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(timeStr).trim());
  if (!dm || !tm) return null;
  const y = Number(dm[1]);
  const mo = Number(dm[2]) - 1;
  const d = Number(dm[3]);
  const hh = Number(tm[1]);
  const mm = Number(tm[2]);
  const dt = new Date(y, mo, d, hh, mm, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Single-line label for owner/tenant/pro: en-US, 12-hour clock, viewer's local timezone (Intl).
 */
export function formatMeetingOptionForViewer(dateStr, timeStr) {
  const dt = parseMeetingDateTimeLocalParts(dateStr, timeStr);
  if (!dt) {
    const parts = [dateStr, timeStr].filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
  }
  return dt.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}
